import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { RecourseBookingCUModel } from '../../content/recourse-booking/model/recourse-booking-cu-model';
import { RecourseBookingModel } from '../../content/recourse-booking/model/recourse-booking-model';
import { completeEndpoint, EndpointPart } from '../../core/global';
import { EndpointService } from '../endpoint/endpoint.service';

/**
 * @deprecated Use RecourseBookingService (or similar if renamed) instead.
 */
@Injectable({
  providedIn: 'root',
})
export class BookingService {
  private readonly http = inject(HttpClient);
  private readonly endpointService = inject(EndpointService);

  public getRecourseBookingPendingApprovals(): Observable<RecourseBookingModel[]> {
    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.GET_BOOKING_PENDING_APPROVALS),
    );
    return this.http.get<RecourseBookingModel[]>(url);
  }

  public approveOrRejectBooking(recourseBookingId: number, approve: boolean, rejectComment?: string): Observable<any> {
    const pathParams: Map<string, number | boolean | string> = new Map();
    pathParams.set('recourseBookingId', recourseBookingId);
    const requestParams = new HttpParams().set('approve', approve.toString()).set('rejectComment', rejectComment);

    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.PATCH_APPROVE_BOOKING),
      pathParams,
    );

    return this.http.patch(url, null, { params: requestParams });
  }

  public reprocessBooking(recourseBookingId: number): Observable<any> {
    const pathParams: Map<string, number | boolean | string> = new Map();
    pathParams.set('recourseBookingId', recourseBookingId);

    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.PATCH_REPROCESS_BOOKING),
      pathParams,
    );

    return this.http.patch(url, null);
  }

  public createNewRecourseBooking(recourseCUBooking: RecourseBookingCUModel): Observable<any> {
    const url: string = this.endpointService.getEndpointUrl(completeEndpoint(EndpointPart.POST_NEW_RECOURSE_BOOKING));
    return this.http.post<any>(url, recourseCUBooking);
  }

  public getRecourseBookingDetails(recourseBookingId: number, isEditMode?: boolean): Observable<RecourseBookingModel> {
    const pathParams: Map<string, number> = new Map();
    pathParams.set('recourseBookingId', recourseBookingId);
    const requestParams = new HttpParams().set('isEditMode', Boolean(isEditMode).toString());

    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.GET_BOOKING_DETAILS),
      pathParams,
    );
    return this.http.get<RecourseBookingModel>(url, { params: requestParams });
  }

  public getCurrencyKeys(): Observable<string[]> {
    const pathParams: Map<string, string> = new Map();
    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.GET_ALL_CURRENCY_KEYS),
      pathParams,
    );
    return this.http.get<string[]>(url);
  }

  public updateRecourseBookingDetails(recourseBooking: RecourseBookingCUModel): Observable<any> {
    const pathParams: Map<string, number> = new Map();
    pathParams.set('recourseBookingId', Number(recourseBooking.pkBookingId));

    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.PUT_BOOKING_DETAILS),
      pathParams,
    );
    return this.http.put<any>(url, recourseBooking);
  }

  public cancelBooking(recourseBookingId: number): Observable<any> {
    const pathParams: Map<string, number> = new Map();
    pathParams.set('recourseBookingId', Number(recourseBookingId));

    const url: string = this.endpointService.getEndpointUrl(completeEndpoint(EndpointPart.CANCEL_BOOKING), pathParams);
    return this.http.patch<any>(url, null);
  }

  public startApprovalForAftersalesAutomatedBookings(): Observable<any> {
    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.POST_APPROVE_AUTOMATED_AFTERSALES_BOOKINGS),
    );
    return this.http.post<any>(url, null);
  }

  public startApproval(recourseBookingId: number): Observable<any> {
    const pathParams: Map<string, number> = new Map();
    pathParams.set('recourseBookingId', Number(recourseBookingId));

    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.PATCH_START_APPROVAL_BOOKING),
      pathParams,
    );
    return this.http.patch<any>(url, null);
  }

  public startApprovalForNewRecourseBooking(recourseBooking: RecourseBookingCUModel): Observable<any> {
    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.POST_START_APPROVAL_NEW_BOOKING),
    );
    return this.http.post<any>(url, recourseBooking);
  }

  public getRecourseBookingReferenceBookings(referencedBookingId: number): Observable<RecourseBookingModel[]> {
    const pathParams: Map<string, number> = new Map();
    pathParams.set('referencedBookingId', referencedBookingId);

    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.GET_REFERENCE_BOOKINGS),
      pathParams,
    );
    return this.http.get<RecourseBookingModel[]>(url);
  }
}
